import { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../translations/translations';
import { CONTACT_FORM_ENDPOINT } from '../utils/api';

const ContactSection = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    institution: '',
    phone: '',
    email: '',
    // address: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    
    // Validate phone number if it's the phone field
    if (e.target.name === 'phone') {
      const phoneValue = e.target.value;
      if (phoneValue && !/^\d{10}$/.test(phoneValue)) {
        setPhoneError(getTranslation(language, 'messages.phoneError'));
      } else {
        setPhoneError('');
      }
    }
    
    // Validate email if it's the email field
    if (e.target.name === 'email') {
      const emailValue = e.target.value;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailValue && !emailRegex.test(emailValue)) {
        setEmailError(getTranslation(language, 'messages.emailError'));
      } else {
        setEmailError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setPhoneError('');
    setEmailError('');
    
    // Validate phone number before submission
    if (!formData.phone || !/^\d{10}$/.test(formData.phone)) {
      setPhoneError(getTranslation(language, 'messages.phoneError'));
      setIsSubmitting(false);
      return;
    }
    
    // Validate email before submission
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      setEmailError(getTranslation(language, 'messages.emailError'));
      setIsSubmitting(false);
      return;
    }
    
    try {
      const payload = {
        name: formData.name,
        institute_name: formData.institution,
        mob: parseInt(formData.phone),
        email: formData.email
        // address: formData.address
      };

      const response = await axios.post(CONTACT_FORM_ENDPOINT, payload);
      const result = response.data;

      if (result.includes('ALLFIELDREQUIRED') || result === 'ALLFIELDREQUIRED') {
        setError(getTranslation(language, 'messages.allFieldsRequired'));
        setIsSubmitting(false);
      } else if (result.includes('SENDMAIL') || result === 'SENDMAIL') {
        setIsSubmitting(false);
        setSubmitted(true);
        setFormData({ name: '', institution: '', phone: '', email: '' /* address: '' */ });
        
        // Reset success message after 5 seconds
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        setError(getTranslation(language, 'messages.unexpectedError'));
        setIsSubmitting(false);
      }
    } catch (err) {
      setError(getTranslation(language, 'messages.submissionError'));
      setIsSubmitting(false);
      console.error('Form submission error:', err);
    }
  };

  return (
    <section className="section-padding contact-section" id="contact">
      <div className="container">
        <div className="section-title" data-aos="fade-up">
          <h2>{getTranslation(language, 'contact.title')}</h2>
          <p>
            {getTranslation(language, 'contact.subtitle')}
          </p>
        </div>
        
        <div className="row g-4">
          <div className="col-lg-7" data-aos="fade-right">
            <div className="contact-form-wrapper">
              <h3>{getTranslation(language, 'contact.bookDemo')}</h3>
              <p>{getTranslation(language, 'contact.fillDetails')}</p>
              
              {submitted && (
                <div className="alert alert-success mb-4" role="alert">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {getTranslation(language, 'contact.thankYou')}
                </div>
              )}
              
              {error && (
                <div className="alert alert-danger mb-4" role="alert">
                  <i className="bi bi-exclamation-circle-fill me-2"></i>
                  {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">{getTranslation(language, 'contact.yourName')} *</label>
                    <input 
                      type="text" 
                      className="form-control form-control-custom" 
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={getTranslation(language, 'contact.yourName')}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">{getTranslation(language, 'contact.institutionName')} *</label>
                    <input 
                      type="text" 
                      className="form-control form-control-custom" 
                      name="institution"
                      value={formData.institution}
                      onChange={handleChange}
                      placeholder={getTranslation(language, 'contact.institutionName')}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">{getTranslation(language, 'contact.phoneNumber')} *</label>
                    <input 
                      type="tel" 
                      className={`form-control form-control-custom ${phoneError ? 'is-invalid' : ''}`}
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={getTranslation(language, 'contact.phoneNumber')}
                      maxLength="10"
                      pattern="\d{10}"
                      required
                    />
                    {phoneError && (
                      <div className="invalid-feedback d-block mt-2">
                        {phoneError}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">{getTranslation(language, 'contact.emailAddress')} *</label>
                    <input 
                      type="email" 
                      className={`form-control form-control-custom ${emailError ? 'is-invalid' : ''}`}
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={getTranslation(language, 'contact.emailAddress')}
                      required
                    />
                    {emailError && (
                      <div className="invalid-feedback d-block mt-2">
                        {emailError}
                      </div>
                    )}
                  </div>
                  {/* <div className="col-12">
                    <label className="form-label">{getTranslation(language, 'contact.institutionAddress')} *</label>
                    <textarea 
                      className="form-control form-control-custom" 
                      name="address"
                      rows="4"
                      value={formData.address}  
                      onChange={handleChange}
                      placeholder={getTranslation(language, 'contact.institutionAddress')}
                      required
                    ></textarea>
                  </div> */}
                  <div className="col-12 mt-4">
                    <button 
                      type="submit" 
                      className="btn btn-submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {getTranslation(language, 'contact.submitting')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-calendar-check me-2"></i>
                          {getTranslation(language, 'contact.scheduleDemo')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          
          <div className="col-lg-5" data-aos="fade-left">
            <div className="contact-info-card">
              <img src='TA.png' alt='..' style={{ height: 40, marginBottom: 16, backgroundColor: 'white', padding: 8 }}/>
              
              <div className="contact-info-item">
                <i className="bi bi-geo-alt-fill"></i>
                <div>
                  <h6>{getTranslation(language, 'contact.officeAddress')}</h6>
                  <p className='fs-6 fw-bold'>Sangli office</p>
                  <p className='m-0'>{getTranslation(language, 'contact.officeDesc')}</p><br/>
                  <p className='fs-6 fw-bold'>Pune office</p>
                  <p>{getTranslation(language, 'contact.puneAddr')}</p>
                </div>
              </div>
              
              <div className="contact-info-item">
                <i className="bi bi-telephone-fill"></i>
                <div>
                  <h6>{getTranslation(language, 'contact.phoneNum')}</h6>
                  <p>{getTranslation(language, 'contact.phoneNumVal')}</p>
                </div>
              </div>
              
              <div className="contact-info-item">
                <i className="bi bi-envelope-fill"></i>
                <div>
                  <h6>{getTranslation(language, 'contact.emailAddr')}</h6>
                  <p>{getTranslation(language, 'contact.emailVal')}</p>
                </div>
              </div>
              
              <div className="contact-info-item">
                <i className="bi bi-clock-fill"></i>
                <div>
                  <h6>{getTranslation(language, 'contact.businessHours')}</h6>
                  <p>{getTranslation(language, 'contact.businessDesc')}</p>
                </div>
              </div>
              
              <hr className="my-4" style={{ borderColor: 'rgba(255,255,255,0.2)' }} />
              
              <div className="d-flex gap-3">
                <a href="/" className="btn btn-outline-light rounded-circle" style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="bi bi-facebook"></i>
                </a>
                <a href="/" className="btn btn-outline-light rounded-circle" style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="bi bi-linkedin"></i>
                </a>
                <a href="/" className="btn btn-outline-light rounded-circle" style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="bi bi-twitter-x"></i>
                </a>
                <a href="/" className="btn btn-outline-light rounded-circle" style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="bi bi-youtube"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
